<template>
  <!-- Innovadis custom anchor link -->
  <div id="all-variations"></div>
  <VcWidget
    v-if="!model.hidden"
    class="border-none shadow-none rounded-lg"
    :class="[
      'variations',
      {
        'variations--full-view': isFullView && !isSmallScreen,
      },
    ]"
    size="lg"
  >
    <template v-if="!isSmallScreen && isTableView" #append>
      <VcButton
        :icon="isFullView ? 'delete-2' : 'arrows-expand'"
        color="neutral"
        variant="no-border"
        size="sm"
        @click="isFullView = !isFullView"
      />
    </template>

    <div class="variations__views flex flex-row w-full">
      <div v-if="!isSmallScreen" class="variations__view-mode grow">
        <div class="flex flex-row w-full">
          <VcTypography tag="h2" variant="h3" class="grow text-[--color-body-text]">{{
            model.title || $t("shared.catalog.product_details.variations.title")
          }}</VcTypography>

          <VcTabSwitch
            v-model="isTableView"
            :value="false"
            icon="list"
            :label="$t('shared.catalog.product_details.variations.list')"
            @change="toggleView"
          />

          <VcTabSwitch
            v-model="isTableView"
            :value="Boolean(true)"
            icon="table"
            :label="$t('shared.catalog.product_details.variations.table')"
            @change="toggleView"
          />
        </div>
      </div>

      <InnoButton variant="outline" shape="pill" @click="$emit('showFilters')">
        {{ $t("common.buttons.filters") }}
      </InnoButton>
    </div>

    <!-- Filters chips -->
    <ActiveFilterChips
      v-if="hasSelectedFilters"
      :filters="productsFilters?.filters"
      :facets-to-hide="['productfamilyid']"
      @apply-filters="applyFiltersOnly"
      @reset-filters="$emit('resetFilters')"
    />

    <InnoVariationsDefault
      v-if="isSmallScreen || (!isSmallScreen && !isTableView)"
      :variations="variations.filter((v) => v.id !== props.productId)"
      :fetching="fetchingVariations"
      :page-number="pageNumber"
      :pages-count="pagesCount"
      @change-page="changePage"
    />

    <InnoVariationsTable
      v-else
      :variations="variations.filter((v) => v.id !== props.productId)"
      :sort="sort"
      :fetching="fetchingVariations"
      :page-number="pageNumber"
      :pages-count="pagesCount"
      @apply-sorting="applySorting"
      @change-page="changePage"
    />

    <VcEmptyView
      v-if="variations.length === 0 && !fetchingVariations"
      :text="$t('shared.catalog.product_details.variations.no_results')"
      icon="outline-stock"
    />
  </VcWidget>
</template>

<script setup lang="ts">
import { useBreakpoints } from "@vueuse/core";
import { onBeforeUnmount, onMounted, ref, toRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useAnalytics } from "@/core/composables";
import { BREAKPOINTS } from "@/core/constants";
import InnoVariationsDefault from "./inno-variations-default.vue";
import InnoVariationsTable from "./inno-variations-table.vue";
import InnoButton from "@/ui-kit/components/molecules/innovadis/inno-button.vue";
import type { Product, SearchProductFilterResult } from "@/core/api/graphql/types";
import type { ISortInfo } from "@/core/types";
import type { ProductsFiltersType } from "@/shared/catalog";
import ActiveFilterChips from "@/shared/catalog/components/active-filter-chips.vue";

interface IEmits {
  (event: "applySorting", item: ISortInfo): void;
  (event: "changePage", pageNumber: number): void;
  (event: "showFilters"): void;
  (event: "resetFilters"): void;
  (event: "applyFilters", value: ProductsFiltersType): void;
}

interface IProps {
  variations: Product[];
  fetchingVariations: boolean;
  sort: ISortInfo;
  model: {
    title?: string;
    hidden?: boolean;
  };
  pageNumber: number;
  pagesCount: number;
  productsFilters?: ProductsFiltersType;
  hasSelectedFilters: boolean;
  productId: string;
  productName: string;
}

const emit = defineEmits<IEmits>();

const props = defineProps<IProps>();

const variations = toRef(props, "variations");

const { analytics } = useAnalytics();
const { t } = useI18n();
const breakpoints = useBreakpoints(BREAKPOINTS);

const isFullView = ref(false);
const isTableView = ref(false);
const isSmallScreen = breakpoints.smaller("xl");

function toggleView() {
  isTableView.value = !isTableView.value;
  isFullView.value = false;
}

function applySorting(sortInfo: ISortInfo): void {
  emit("applySorting", sortInfo);
}

function changePage(page: number): void {
  emit("changePage", page);
}

function applyFiltersOnly(newFilters: SearchProductFilterResult[]) {
  emit("applyFilters", {
    ...props.productsFilters,
    filters: newFilters,
    facets: props.productsFilters?.facets || [],
    inStock: props.productsFilters?.inStock || false,
    purchasedBefore: props.productsFilters?.purchasedBefore || false,
    branches: props.productsFilters?.branches || [],
  });
}

onMounted(() => {
  window.addEventListener("keyup", handleKeyUp);
});

onBeforeUnmount(() => {
  window.removeEventListener("keyup", handleKeyUp);
});

function handleKeyUp(event: KeyboardEvent) {
  if (event.key === "Escape") {
    isFullView.value = false;
  }
}

watch(
  variations,
  (variationsValue) => {
    if (!variationsValue.length) {
      return;
    }

    analytics("viewItemList", variationsValue, {
      item_list_id: "variations",
      item_list_name: `${t("shared.catalog.product_details.variations.title")} ${props.productName}`,
      related_id: props.productId,
      related_type: "product",
    });
  },
  { immediate: true },
);
</script>

<style lang="scss">
.variations {
  .vc-tab-switch button {
    @apply border-none shadow-none;

    span.vc-tab-switch__label {
      @apply hidden;
    }
  }

  &--full-view {
    @media (width >= theme("screens.xl")) {
      --variations-margin-x: theme("margin.4");

      @apply z-10 shadow-2xl;

      width: calc(100vw - var(--variations-margin-x) * 2);
      margin-left: calc((var(--vc-container-offset) - var(--variations-margin-x)) * -1);
    }
  }

  &__views {
    @apply flex justify-between items-center mb-6 gap-3;
  }

  &__view-mode {
    @apply space-x-2;
  }

  &__chips {
    @apply flex flex-wrap gap-x-3 gap-y-2 pb-6;
  }
}
</style>
